import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchSupportHistory } from "../services/supportHistoryService";
import type { SupportMeeting } from "../types/support.types";

export type SupportHistoryStatus = "loading" | "ready" | "failed";

export interface SupportHistoryState {
  /** Every call booked, newest first. Empty until the answer lands. */
  meetings: SupportMeeting[];
  status: SupportHistoryStatus;
  error?: string;
  reload: () => void;
}

const EMPTY: SupportMeeting[] = [];

export const SUPPORT_HISTORY_QUERY_KEY = ["settings", "support-history"] as const;

/**
 * What this workspace has booked, from `GET /api/v1/support-history/`.
 *
 * Two things read it, and the second is the reason it must not fail quietly:
 * the history under the form is drawn from these rows, and the form itself
 * refuses a second booking while one is still ahead. A failed load comes back
 * `failed` with no rows, and the tab leaves the form open on it — the rule is
 * worth enforcing on an answer and not on the absence of one, since a dietitian
 * shut out of support by a network error has no other way to reach us.
 *
 * Backed by TanStack Query: a screen visited again shows the last-known
 * booked calls immediately while a fresh read runs behind it, so a call
 * booked, moved or called off on another device — or in the Cal invite's own
 * email — still catches up within moments instead of making every visit wait
 * on the network. A load with nothing cached to fall back on still fails
 * outright: a row offering Join on a call that was never confirmed booked is
 * worse than no row, and that only risks happening on a first, uncached load.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useSupportHistory(): SupportHistoryState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: SUPPORT_HISTORY_QUERY_KEY,
    queryFn: ({ signal }) => fetchSupportHistory(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("support history failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  return {
    meetings: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export default useSupportHistory;
